'use client'

import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { SelectDropdown } from '@/components/select-dropdown'
import { User } from '../data/schema'
import { useTranslation } from 'react-i18next'
import { add_user, check_username_unique, update_user } from '@/api/user'
import { UsersTableParams } from '..'
import { debounceAsync } from '@/utils/debounce'
import { handleServerError } from '@/utils/handle-server-error'
import { useEffect } from 'react'
import { useRoles } from '@/views/roles-permissions/hooks/use-roles'

interface Props {
  currentRow?: User
  open: boolean
  onOpenChange: (open: boolean) => void
  params: UsersTableParams
  setParams: React.Dispatch<React.SetStateAction<UsersTableParams>>
}

export function UsersActionDialog({ currentRow, open, onOpenChange, params, setParams }: Props) {
  const i18n = useTranslation()
  const { data: roles = [], isLoading: isLoadingRoles } = useRoles()
  const isEdit = !!currentRow
  const check_username = debounceAsync(check_username_unique, 200)
  const formSchema = z
    .object({
      name: z.string().min(1, { message: i18n.t('name_required') }),
      username: z.string()
        .superRefine(
          async (username, ctx) => {
            if ((isEdit && username == currentRow.username) || username == '') {
              return
            } else if (username.length < 4) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('username_required')
              })
            } else if (!await check_username(username)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: i18n.t('username_taken')
              })
            }
          }
        )
        .nullable(),
      email: z.string().optional().refine((val) => {
        // Only validate if the value is not empty
        if (val === undefined || val.trim() === '') {
          return true; // If it's undefined or empty, it's valid
        }
        return z.string().email().safeParse(val).success
      },
        {
          message: i18n.t('email_invalid')
        }),
      role: z.string().min(1, { message: i18n.t('role_needed') }),
      is_active: z.string()
        .min(1, { message: i18n.t('status_needed') })
    })
  type UserForm = z.infer<typeof formSchema>

  const form = useForm<UserForm>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: '',
      username: '',
      email: '',
      role: '',
      is_active: 'true'
    },
  })

  useEffect(() => {
    if (isEdit && currentRow) {
      form.reset({
        ...currentRow,
        username: currentRow.username === null ? '' : currentRow.username,
        email: currentRow.email === null ? '' : currentRow.email,
        role: currentRow.role.id.toString(),
        is_active: currentRow.is_active ? 'true' : 'false'
      });
    }
  }, [currentRow, isEdit]);

  const onSubmit = (values: UserForm) => {
    const req = {
      name: values.name,
      username: values.username == '' ? null : values.username,
      role: Number(values.role),
      is_active: values.is_active == 'true' ? true : false,
      email: values.email === undefined || values.email == null || values.email == '' ? null : values.email
    }

    if (isEdit && currentRow) {
      const noChanges =
        currentRow.name === req.name &&
        currentRow.username === req.username &&
        currentRow.role.id === req.role &&
        currentRow.is_active === req.is_active &&
        currentRow.email === req.email;

      if (noChanges) {
        onOpenChange(false);
        return;
      }
    }

    const future = isEdit
      ? update_user(currentRow?.id, req)
      : add_user(req)

    future
      .then(() => {
        form.reset()
        toast({
          title: i18n.t(isEdit ? 'user_updated' : 'user_added', { user: values.name }),
        })
        onOpenChange(false)
        setParams({ ...params, page: 0 })
      })
      .catch(e => {
        handleServerError(e)
      });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        form.reset()
        onOpenChange(state)
      }}
    >
      <DialogContent className='sm:max-w-rg'>
        <DialogHeader className='text-right'>
          <DialogTitle>{isEdit ? i18n.t('edit_user') : i18n.t('add_user')}</DialogTitle>
          <DialogDescription>
            {i18n.t('user_action')}
          </DialogDescription>
        </DialogHeader>
        <div className='-mr-4 h-[23.25rem] w-full overflow-y-auto py-1 pr-4'>
          <Form {...form}>
            <form
              id='user-form'
              onSubmit={form.handleSubmit(onSubmit, (errors) => {
                const messages = Object.values(errors)
                  .map((e: any) => e?.message)
                  .filter(Boolean) as string[]
                toast({
                  title: i18n.t('validation_failed'),
                  description: messages.join(' · '),
                  variant: 'destructive',
                })
              })}
              className='space-y-4 p-0.5'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {i18n.t('name')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={i18n.t('name_placeholder')}
                        className='col-span-4'
                        autoComplete='off'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='is_active'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {i18n.t('status')}
                    </FormLabel>
                    <SelectDropdown
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      placeholder={i18n.t('select_account_status')}
                      className='col-span-4'
                      items={[
                        { label: i18n.t('status_locked'), value: 'false' },
                        { label: i18n.t('status_active'), value: 'true' }
                      ]}
                    />
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='username'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {i18n.t('user')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder='mohamed.ali23'
                        className='col-span-4'
                        {...field}
                        value={field.value || ''}
                        onBlur={() => form.trigger("username")}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {i18n.t('email')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder='mohamed.ali@gmail.com'
                        className='col-span-4'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='role'
                render={({ field }) => (
                  <FormItem className='grid grid-cols-6 items-center gap-x-4 gap-y-1 space-y-0'>
                    <FormLabel className='col-span-2 text-right'>
                      {i18n.t('role')}
                    </FormLabel>
                    <SelectDropdown
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      placeholder={i18n.t('select_role')}
                      className='col-span-4'
                      isPending={isLoadingRoles}
                      items={roles.filter((r: any) => {
                        console.log("le role est ", r.name, r.isEnabled, r.is_enabled)
                        const enabled = r.isEnabled !== undefined ? r.isEnabled : (r.is_enabled !== undefined ? r.is_enabled : true);
                        return enabled !== false;
                      }).map((t: any) => ({
                        label: t.name,
                        value: t.id.toString(),
                      }))}
                    />
                    <FormMessage className='col-span-4 col-start-3' />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button type='submit' form='user-form'>
            {i18n.t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
